/* eslint-disable @typescript-eslint/no-explicit-any */
// This is a generic component-override merge system ("overrides pattern") that
// operates on heterogeneous component/override/props shapes and walks the
// config by arbitrary model/attribute names. Precise typing of the plumbing is
// impractical, so `any` is used deliberately here; the meaningful domain types
// (RhinoResource scopes) are kept.
import React, { useMemo } from 'react';
import {
  clone,
  cloneDeep,
  isArray,
  isFunction,
  isString,
  keys,
  merge,
  mergeWith
} from 'lodash-es';
import {
  useModel,
  useModelContext,
  useModelAndAttributeFromPath
} from './models';
import { NullComponent } from '../components/null';
import { useRhinoConfig } from '../config';
import { RhinoResource } from '../index';

// An intrinsic tag name is a component here too: isShortCut accepts a string
// override and JSX renders it as the element of that name.
type AnyComponent = React.ElementType<any>;
type Props = Record<string, any>;

// Based on:
// https://medium.com/@dschnr/better-reusable-react-components-with-the-overrides-pattern-9eca2339f646
// https://github.com/tlrobinson/overrides

const arrayOverride = (objValue: any, srcValue: any) => {
  if (isArray(objValue)) {
    return [...srcValue];
  }
};

const maybeMerge = (a: any, b: any) => {
  return a && b ? { ...a, ...b } : a || b;
};

const isShortCut = (override: any) =>
  override === null ||
  isFunction(override) ||
  isString(override) ||
  override instanceof React.Component;

const expandOverride = (component: any) =>
  isFunction(component) ? { component } : clone(component);

const expandOverrides = (overrides: any) => {
  const scopedOverrides: Record<string, any> = {};

  // overrides can be null or undefined
  keys(overrides).forEach((key) => {
    const component = overrides[key];

    scopedOverrides[key] = expandOverride(component);
  });

  return scopedOverrides;
};

const getOverrides = (
  override: any,
  Component: AnyComponent,
  props: Props = {}
): [AnyComponent, Props] => {
  // component override shortcut:
  if (isShortCut(override)) {
    Component = override || NullComponent;
  } else if (override) {
    const { style, props: propsOverride, component, ...nested } = override;
    // assume at least one of the folllowing will override props, so clone
    props = { ...props };
    // component override:
    if (component) {
      Component = component;
    }
    // props override:
    if (propsOverride) {
      Object.assign(props, propsOverride);
    }
    // style override:
    if (style) {
      props.style = maybeMerge(
        props.style,
        typeof style === 'function' ? style(props) : style
      );
      // TODO: support $style?
      // props.$style = style;
    }
    // nested overrides:
    if (Object.keys(nested).length > 0) {
      props.overrides = maybeMerge(props.overrides, nested);
    }
  }
  return [Component, props];
};

const initializeOverrideWrappers = (
  defaultComponents: Record<string, AnyComponent>,
  getOverridesProp: () => Record<string, any> | null | undefined
) => {
  // initialize component wrappers
  const components: Record<string, React.FC<Props>> = {};
  for (const name of Object.keys(defaultComponents)) {
    components[name] = (props: Props) => {
      const overrides = getOverridesProp() || {};

      const [Component, mergedProps] = getOverrides(
        overrides[name],
        defaultComponents[name],
        props
      );

      return <Component {...mergedProps} />;
    };
    components[name].displayName = `${name}Overridable`;
  }
  return components;
};

export const useOverrides = (
  defaultComponents: Record<string, AnyComponent>,
  overrides?: Record<string, any> | null
) => {
  const overridesRef = React.useRef(overrides);
  // eslint-disable-next-line react-hooks/refs
  overridesRef.current = overrides;
  return React.useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs
      initializeOverrideWrappers(defaultComponents, () => overridesRef.current),
    [defaultComponents]
  );
};

export const useMergedOverrides = (
  baseOverrides?: Record<string, any> | null,
  overrides?: Record<string, any> | null
) => {
  const mergedOverrides = useMemo(() => {
    return merge(
      {},
      expandOverrides(baseOverrides),
      expandOverrides(overrides)
    );
  }, [baseOverrides, overrides]);

  return mergedOverrides;
};

interface OverrideScope {
  attribute?: { name?: string };
  attributeModel?: RhinoResource;
  model?: RhinoResource;
}

export const useGlobalComponent = (
  overrideName: string,
  BaseComponent: AnyComponent,
  props: any,
  scope: OverrideScope = {}
) => {
  const components: any = useRhinoConfig().components;
  const defaultComponents = useMemo(() => {
    return { [overrideName]: BaseComponent };
  }, [overrideName, BaseComponent]);

  const { attribute, attributeModel, model } = scope;

  const computedOverrides = useMemo(() => {
    const scopedOverrides: Record<string, any> = {};

    Object.keys(defaultComponents).forEach((key) => {
      // We can't directly use the globalOverrides object because it will mutate
      // So we shallow clone the globals and merge the overrides on top

      const overrideComponent = [
        // Order from least specific to most specific so that the most specific wins
        // Global overrides
        components[key],
        // Model overrides
        components?.[model?.model ?? '']?.[key],
        // Attribute and attribute model overrides
        components?.[attributeModel?.model ?? '']?.[key],
        components?.[attributeModel?.model ?? '']?.[attribute?.name ?? '']?.[
          key
        ]
      ].filter((override) => override !== undefined);

      if (overrideComponent.length > 0) {
        const expandedOverrides = overrideComponent.map((override) =>
          cloneDeep(expandOverride(override))
        );

        // If null is the last override, then we want to null out the component
        if (expandedOverrides[expandedOverrides.length - 1] === null) {
          scopedOverrides[key] = null;
        } else {
          // Merge from least specific to most specific so that the most specific wins
          scopedOverrides[key] = mergeWith(
            {},
            ...expandedOverrides,
            // The props passed for the component takes precedence over the global overrides
            { props: cloneDeep(expandOverride(props)) },
            arrayOverride
          );
        }
      }
    });

    return mergeWith(
      scopedOverrides,
      // We need to replace arrays instead of merging them, for instance for
      // paths we want to replace
      arrayOverride
    );
  }, [components, defaultComponents, model, attributeModel, attribute, props]);

  const globalOverride = useOverrides(defaultComponents, computedOverrides);
  const GlobalOverrideComponent = globalOverride[overrideName];
  // eslint-disable-next-line react-hooks/immutability
  GlobalOverrideComponent.displayName = overrideName;

  return <GlobalOverrideComponent {...props} />;
};

export const useGlobalComponentForModel = (
  overrideName: string,
  BaseComponent: AnyComponent,
  props: any,
  scope: OverrideScope | null = null
) => {
  const { model } = useModelContext();
  const propModel = useModel(props?.model);

  // The top level model is not always available right now
  // ModelIndex, ModelShow, ModelCreate, ModelEdit have model as a prop still
  // So we need to check if the model is available in the props
  // FIXME: This is a temporary solution, we should always use the model from the context
  const modelScope = useMemo(
    () => (propModel ? { model: propModel, ...scope } : { model, ...scope }),
    [model, propModel, scope]
  );

  return useGlobalComponent(overrideName, BaseComponent, props, modelScope);
};

export const useGlobalComponentForAttribute = (
  overrideName: string,
  BaseComponent: AnyComponent,
  props: any,
  scope: OverrideScope | null = null
) => {
  const { model } = useModelContext();
  const propModel = useModel(props?.model);

  // The top level model is not always available right now
  // ModelIndex, ModelShow, ModelCreate, ModelEdit have model as a prop still
  // So we need to check if the model is available in the props
  // FIXME: This is a temporary solution, we should always use the model from the context
  const modelScope = useMemo(
    () => (propModel ? { model: propModel, ...scope } : { model, ...scope }),
    [model, propModel, scope]
  );

  const { model: attributeModel, attribute } = useModelAndAttributeFromPath(
    (propModel || model)!,
    props?.path
  );

  const attributeScope = useMemo(
    () => ({ attributeModel, attribute, ...modelScope }),
    [attribute, attributeModel, modelScope]
  );

  return useGlobalComponentForModel(
    overrideName,
    BaseComponent,
    props,
    attributeScope
  );
};
